/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Fade,
  DialogActions,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import { ArchiveOutlined, NewReleasesOutlined } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import useGetResource from "@/hooks/useGetResources";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return (
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }) +
    " " +
    date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
  );
};

// Check if news is new (created within last 24 hours)
const isNewAnnouncement = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffHours = (now - created) / (1000 * 60 * 60);
  return diffHours < 24;
};

const AnnouncementCard = ({ announcement, onReadMore, onDelete }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const preview =
    announcement.body.length > 120
      ? `${announcement.body.slice(0, 120)}...`
      : announcement.body;

  // Find the first image attachment (e.g., JPEG/PNG)
  const previewImage = announcement.attachments?.find(
    (att) => att.is_image && att.url
  );

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          position: "relative",
          p: 1, // Padding creates stable hover zone
          m: -0.5, // Negative margin to maintain spacing
          borderRadius: 4,
          "&:hover .card-container": {
            transform: "translateY(-6px)",
            boxShadow: theme.shadows[8],
            borderColor: "primary.main",
            "& .archive-card-image": {
              transform: "scale(1.05)",
            },
          },
        }}
      >
        <Card
          elevation={2}
          className="card-container"
          sx={{
            p: 3,
            height: 220,
            display: "flex",
            flexDirection: "row",
            position: "relative",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: "pointer",
            willChange: "transform, box-shadow",
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
          }}
          onClick={onReadMore}
          aria-label={t("archive.cardAria", { title: announcement.title })}
        >
          {/* Image Preview on Left */}
          {previewImage ? (
            <CardMedia
              component="img"
              image={previewImage.url}
              alt={previewImage.display_name}
              className="archive-card-image"
              sx={{
                width: 150,
                height: "100%",
                objectFit: "cover",
                borderRadius: 2,
                transition: "transform 0.3s ease",
              }}
            />
          ) : (
            <Box
              className="archive-card-image"
              sx={{
                width: 150,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontStyle: "italic",
                color: "text.secondary",
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
                transition: "transform 0.3s ease",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <ArchiveOutlined
                  sx={{ fontSize: 32, color: "text.disabled" }}
                />
                <Typography variant="caption" textAlign="center">
                  {t("archive.noImage")}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Content on Right */}
          <Box sx={{ flex: 1, p: 2, overflow: "hidden" }}>
            <Box display="flex" alignItems="center" mb={1}>
              <Avatar
                src={announcement.author.avatar_url}
                alt={
                  announcement.author.first_name || t("archive.userAvatarAlt")
                }
                sx={{ width: 36, height: 36, mr: 1 }}
              />
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    noWrap
                    sx={{ maxWidth: 160 }}
                  >
                    {announcement.author.first_name}{" "}
                    {announcement.author.last_name}
                  </Typography>
                  {isNewAnnouncement(announcement.created_at) && (
                    <Chip
                      icon={<NewReleasesOutlined sx={{ fontSize: 14 }} />}
                      label={t("archive.newChip")}
                      size="small"
                      sx={{
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          0.15
                        ),
                        color: theme.palette.success.dark,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        fontWeight: 600,
                        "& .MuiChip-icon": {
                          color: theme.palette.success.dark,
                        },
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(announcement.created_at)}
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {announcement.title}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.4,
              }}
            >
              {preview}
            </Typography>
          </Box>

          {/* Delete Icon */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete(announcement.id);
            }}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.2),
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                borderColor: alpha(theme.palette.error.main, 0.4),
                transform: "scale(1.1)",
              },
              transition: "all 0.2s ease",
            }}
            color="error"
            aria-label={t("archive.deleteButtonAria", {
              title: announcement.title,
            })}
          >
            <DeleteIcon />
          </IconButton>
        </Card>
      </Box>
    </Fade>
  );
};

export default function Archive() {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [initialTotalCount, setInitialTotalCount] = useState(null);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { handleApiError, setToast } = useApiErrorHandler();
  const { mutate: deleteData } = useDeleteData(
    "/api/v1/organizations/announcements",
    ["news"]
  );
  const currentPageToken = pageTokens.at(-1);
  const queryUrl = currentPageToken
    ? `/api/v1/organizations/announcements?cursor=${currentPageToken}`
    : `/api/v1/organizations/announcements`;

  const {
    data: newsPage,
    isLoading,
    error,
  } = useGetResource(["news", currentPageToken], queryUrl);

  useEffect(() => {
    if (!newsPage?.records) return;

    const index = pageTokens.length - 1;

    // For first page (page 0), we should reset pages array and capture initial total count
    if (index === 0) {
      setPages([newsPage.records]);
      setCurrentPageIndex(0);
      // Capture the initial total count only from the first page
      if (initialTotalCount === null && newsPage.total_count) {
        setInitialTotalCount(newsPage.total_count);
      }
    } else {
      // For subsequent pages, add to pages array
      setPages((prev) => {
        const copy = [...prev];
        copy[index] = newsPage.records;
        return copy;
      });
      if (index > currentPageIndex) setCurrentPageIndex(index);
    }
  }, [newsPage, pageTokens, currentPageIndex, initialTotalCount]);

  const aggregatedNews = useMemo(
    () => pages.slice(0, currentPageIndex + 1).flat(),
    [pages, currentPageIndex]
  );

  const filteredNews = aggregatedNews;

  const hasNext = newsPage?.next_page;

  const handleNext = () => {
    if (newsPage?.next_page)
      setPageTokens((tokens) => [...tokens, newsPage.next_page]);
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteData(itemToDelete);
      setToast({
        open: true,
        message: t("archive.successToast"),
        severity: "success",
      });
      setConfirmOpen(false);
      setItemToDelete(null);
      queryClient.invalidateQueries(["news"]);
    } catch (err) {
      handleApiError(err);
    }
  };

  const openModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedAnnouncement(null);
  };

  if (isLoading && pages.length === 0) {
    return (
      <Box textAlign="center" p={4}>
        <CircularProgress aria-label={t("archive.loadingAria")} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box textAlign="center" p={4}>
        <Typography color="error">{t("archive.errorLoading")}</Typography>
      </Box>
    );
  }
  if (!aggregatedNews.length) {
    return (
      <Box textAlign="center" p={4}>
        <Typography>{t("archive.noAnnouncements")}</Typography>
      </Box>
    );
  }

  const newAnnouncementsCount = aggregatedNews.filter((a) =>
    isNewAnnouncement(a.created_at)
  ).length;

  return (
    <>
      {/* Compact Enterprise Header */}
      <PageHeader
        icon={<ArchiveOutlined />}
        title={t("archive.headerTitle", {
          defaultValue: "Announcement Archive",
        })}
        subtitle={t("archive.headerSubtitle", {
          defaultValue: "Manage and review company announcements",
        })}
        stats={[
          {
            label: `${aggregatedNews.length}/${
              initialTotalCount || aggregatedNews.length
            }`,
          },
        ]}
        t={t}
        translationPrefix="archive"
        compact={true}
      />

      {/* Grid Layout */}
      <Box sx={{ px: 3 }}>
        <Grid container spacing={3}>
          {filteredNews.map((announcement) => (
            <Grid key={announcement.id} size={{ xs: 12, md: 6 }}>
              <AnnouncementCard
                announcement={announcement}
                onReadMore={() => openModal(announcement)}
                onDelete={(id) => confirmDelete(id)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!hasNext}
          size="large"
          aria-label={t("archive.nextPageAria")}
        >
          {t("archive.loadMoreButton")}
        </Button>
      </Box>

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        aria-labelledby="announcement-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ p: 3 }} id="announcement-dialog-title">
          {selectedAnnouncement?.title}
          <IconButton
            aria-label={t("archive.closeModalAria")}
            onClick={closeModal}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              src={selectedAnnouncement?.author.avatar_url}
              alt={`${selectedAnnouncement?.author.first_name} ${t("archive.avatarAlt")}`}
              sx={{ width: 48, height: 48, mr: 2 }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {selectedAnnouncement?.author.first_name}{" "}
                {selectedAnnouncement?.author.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(selectedAnnouncement?.created_at)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" paragraph>
            {selectedAnnouncement?.body}
          </Typography>

          <Grid container spacing={2}>
            {selectedAnnouncement?.attachments?.map(
              (attachment) =>
                attachment.is_image && (
                  <Grid size={{ xs: 12 }} key={attachment.id}>
                    <CardMedia
                      component="img"
                      src={attachment.url}
                      alt={attachment.display_name}
                      sx={{
                        borderRadius: 2,
                        cursor: "pointer",
                        boxShadow: 3,
                        "&:hover": { opacity: 0.8 },
                      }}
                      onClick={() => {
                        const attachments =
                          selectedAnnouncement?.attachments.filter(
                            (att) => att.is_image
                          ) || [];
                        const index = attachments.findIndex(
                          (att) => att.id === attachment.id
                        );
                        setImageList(attachments);
                        setActiveImageIndex(index);
                        setImageModalOpen(true);
                      }}
                    />
                  </Grid>
                )
            )}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="image-viewer-dialog-title"
      >
        <DialogTitle id="image-viewer-dialog-title">
          <IconButton
            aria-label={t("archive.closeImageViewerAria")}
            onClick={() => setImageModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, textAlign: "center", position: "relative" }}>
          {imageList.length > 0 && (
            <>
              {/* Current Image */}
              <Box
                component="img"
                src={imageList[activeImageIndex]?.url}
                alt={imageList[activeImageIndex]?.display_name}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  borderRadius: 2,
                  transition: "all 0.4s ease",
                }}
              />

              {/* Prev Button */}
              {activeImageIndex > 0 && (
                <IconButton
                  onClick={() => setActiveImageIndex((prev) => prev - 1)}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "background.paper",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  aria-label={t("archive.prevImageAria")}
                >
                  {"<"}
                </IconButton>
              )}

              {/* Next Button */}
              {activeImageIndex < imageList.length - 1 && (
                <IconButton
                  onClick={() => setActiveImageIndex((prev) => prev + 1)}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "background.paper",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  aria-label={t("archive.nextImageAria")}
                >
                  {">"}
                </IconButton>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Deletion Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-deletion-dialog-title"
      >
        <DialogTitle id="confirm-deletion-dialog-title">
          {t("archive.confirmDeleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("archive.confirmDeleteMessage")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t("archive.cancelButton")}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            aria-label={t("archive.deleteConfirmButtonAria")}
          >
            {t("archive.deleteButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
